import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Container } from './layout/container';
import { Button } from './ui/button';
import { LanguageContext } from '../context/LanguageContext';
import { trackError } from '../utils/analytics';

class ErrorBoundary extends React.Component {
  static contextType = LanguageContext;

  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
    
    // Track error with analytics if available
    trackError(error.toString(), 'ErrorBoundary');
  }

  handleReset = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const t = this.context?.t || {};
      const eb = t.errorBoundary || {};

      return (
        <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center p-4">
          <Container>
            <motion.div
              className="text-center max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Error Icon */}
              <motion.div
                className="flex justify-center mb-6"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-10 w-10 text-red-400" />
                </div>
              </motion.div>

              {/* Error Message */}
              <motion.div
                className="space-y-4 mb-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h1 className="text-2xl sm:text-3xl font-semibold text-white">
                  {eb.title || 'Oops! Something went wrong'}
                </h1>
                <p className="text-white/50 max-w-md mx-auto">
                  {eb.description || "Don't worry, it's not your fault. We've been notified and will fix this soon."}
                </p>
                
                {/* Show error details in development */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-sm text-white/40 hover:text-white/60 transition-colors">
                      {eb.technicalDetails || 'Technical Details (Dev Only)'}
                    </summary>
                    <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-xs text-red-400 font-mono mb-2">
                        {this.state.error.toString()}
                      </p>
                      <pre className="text-xs text-white/40 overflow-auto max-h-40">
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </div>
                  </details>
                )}
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Button onClick={this.handleReset} size="lg">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {eb.tryAgain || 'Try Again'}
                </Button>
                <Button 
                  variant="secondary" 
                  size="lg"
                  onClick={() => window.location.href = '/'}
                >
                  <Home className="h-4 w-4 mr-2" />
                  {eb.goHome || 'Go Home'}
                </Button>
              </motion.div>

              {/* Help Text */}
              <motion.p
                className="mt-8 text-xs text-white/30"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                If this keeps happening, please{' '}
                <a 
                  href="/contact-me" 
                  className="text-white/50 hover:text-white transition-colors underline"
                >
                  contact me
                </a>
              </motion.p>
            </motion.div>
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
